// src/resources/dashboards/ProductEvolution/types.ts
//
// Contrato de GET /admin/metrics/portfolio (o payload chega em data[0]).
// Espelha src/api/admin-metrics/admin-metrics-rollup.ts do pontuax-backend —
// mudou lá, muda aqui.

/** Status de ciclo de vida da rede. Excludentes, decididos no backend (lifecycleStatus). */
export type ChainLifecycleStatus = 'cancelada' | 'implantacao' | 'parada' | 'em_risco' | 'ativa';

export type RiskLevel = 'high' | 'medium' | 'low';

/** Sinal que compõe o churnScore. O texto já vem pronto pra leitura do backend. */
export type ChurnSignal =
  | 'movimento'
  | 'postos_parados'
  | 'base_esfriando'
  | 'financeiro'
  | 'uso_produto'
  | 'dono_ausente'
  | 'satisfacao';

export interface ChurnReason {
  signal: ChurnSignal;
  weight: number;
  text: string;
}

export interface PortfolioChainRow {
  chainId: string;
  name: string;
  status: ChainLifecycleStatus;
  churnScore: number;
  riskLevel: RiskLevel;
  /** Ordenado por contribuição decrescente. É o ponto da tela: o porquê do risco. */
  reasons: ChurnReason[];
  avgTicket: number;
  avgTicketDelta: number | null;
  /** 1 = pior quintil da carteira, 5 = melhor. null quando não há amostra. */
  avgTicketQuintile: number | null;
  litersPerFillin: number;
  pricePerLiter: number;
  fillins: number;
  fillinsDelta: number | null;
  revenue: number;
  /** null = rede sem dado de pista (sem concentrador ou Redshift indisponível). */
  appPenetration: number | null;
  activeCustomers: number;
  activeCustomersDelta: number | null;
  newCustomers: number;
  totalCustomers: number;
  activeModules: number;
  enabledModules: number;
  mrr: number;
  overdueBills: number;
  overdueValue: number;
  /** Potencial DECLARADO: os preços de módulo não entram na cobrança real. */
  upsellPotential: number;
  /** Módulos contratados e sem uso na janela. */
  downgradeRisk: string[];
  /** Postos ativos sem abastecimento nos últimos 7 dias (ou com erro em aberto). */
  stalledPlaces: number;
  activePlaces: number;
  canceledAt: string | null;
}

export interface PortfolioKpis {
  activeChains: number;
  stalledChains: number;
  highRiskChains: number;
  implantationChains: number;
  mrr: number;
  uncapturedRevenue: number;
  uncapturedPlaces: number;
  /** Postos valorados pela mediana da carteira por falta de preço próprio. */
  uncapturedEstimatedPlaces: number;
  upsellPotential: number;
  totalFillins: number;
  avgTicketMedian: number | null;
}

export interface PortfolioData {
  id: 'portfolio';
  /** Rollup sendo construído: os campos pesados chegam vazios e o front faz poll. */
  computing: boolean;
  computedAt: string | null;
  /** Último dia coberto por fillin_summary (D-1). Menos que ontem = defasagem. */
  dataThrough: string | null;
  windowDays: number;
  kpis: PortfolioKpis | null;
  /** Só a página pedida. O tamanho da carteira inteira está em `total`. */
  chains: PortfolioChainRow[];
  total: number;
}

/** Janelas suportadas pelo rollup. Fixas: 3 chaves de rollup, nunca mais. */
export const PORTFOLIO_WINDOWS = [30, 60, 90] as const;
export type PortfolioWindow = typeof PORTFOLIO_WINDOWS[number];

/** Colunas ordenáveis — o backend valida exatamente esta lista (Joi valid). */
export type PortfolioOrderBy =
  | 'churnScore'
  | 'avgTicket'
  | 'avgTicketDelta'
  | 'fillins'
  | 'fillinsDelta'
  | 'appPenetration'
  | 'activeCustomers'
  | 'mrr'
  | 'overdueValue'
  | 'upsellPotential'
  | 'name';

export type OrderDir = 'asc' | 'desc';

export const STATUS_LABEL: Record<ChainLifecycleStatus, string> = {
  cancelada: 'Cancelada',
  implantacao: 'Em implantação',
  parada: 'Parada',
  em_risco: 'Em risco',
  ativa: 'Ativa',
};

export const RISK_LABEL: Record<RiskLevel, string> = {
  high: 'Alto',
  medium: 'Médio',
  low: 'Baixo',
};

/** Rótulo do sinal, pra agrupar os motivos na expansão da linha. */
export const SIGNAL_LABEL: Record<ChurnSignal, string> = {
  movimento: 'Movimento',
  postos_parados: 'Postos parados',
  base_esfriando: 'Base de clientes',
  financeiro: 'Financeiro',
  uso_produto: 'Uso do produto',
  dono_ausente: 'Acesso ao admin',
  satisfacao: 'Satisfação',
};

// ─────────────────────────────────────────────────────────────────────────────
// Tela 2: DETALHE da rede. Sete endpoints, um por seção — cada payload chega em
// data[0]. Espelha src/api/admin-metrics/admin-metrics-chain.ts do
// pontuax-backend; mudou lá, muda aqui.
// ─────────────────────────────────────────────────────────────────────────────

/** Granularidade escolhida pelo backend em função do tamanho do período. */
export type BucketUnit = 'day' | 'week';

/** Métrica com comparação contra o período anterior. `deltaPct` é fração (0.12 = 12%). */
export interface ChainKpi {
  value: number;
  previous: number;
  deltaPct: number | null;
}

/** Filtro de período das 7 seções. `to` já sai EXCLUSIVO daqui. */
export interface ChainDetailFilter {
  from: string;
  to: string;
}

// ─── 1. chain-overview ────────────────────────────────────────────────────────

export interface ChainRetailer {
  id: string;
  name: string;
  places: number;
}

export interface ChainHeader {
  chainId: string;
  name: string;
  isActive: boolean;
  canceledAt: string | null;
  cancelReason: string | null;
  createdAt: string | null;
  places: { active: number; total: number };
  mrr: number;
  firstBillingStartDate: string | null;
  /** Revenda majoritária. Uma rede pode ter mais de uma — ver `retailers`. */
  retailer: ChainRetailer | null;
  retailers: ChainRetailer[];
}

export interface ChainOverviewBucket {
  bucket: string;
  revenue: number;
  liters: number;
  fillins: number;
  avgTicket: number;
}

export interface ChainOverviewData {
  id: 'chain-overview';
  chain: ChainHeader;
  /** Último dia coberto por fillin_summary (D-1). Menos que ontem = defasagem. */
  dataThrough: string | null;
  bucketUnit: BucketUnit;
  series: ChainOverviewBucket[];
  kpis: {
    avgTicket: ChainKpi;
    revenue: ChainKpi;
    fillins: ChainKpi;
    liters: ChainKpi;
    litersPerFillin: ChainKpi;
    pricePerLiter: ChainKpi;
  };
  /** 1 = pior quintil de ticket da carteira, 5 = melhor. null sem amostra. */
  avgTicketQuintile: number | null;
  portfolioAvgTicketMedian: number | null;
}

// ─── 2. chain-ticket ──────────────────────────────────────────────────────────

export interface TicketByOrigin {
  /** Valor do enum `origin_t`; null quando o abastecimento não declarou origem. */
  origin: string | null;
  label: string;
  fillins: number;
  revenue: number;
  liters: number;
  avgTicket: number;
  litersPerFillin: number;
  pricePerLiter: number;
  /** Parcela dos abastecimentos da rede que veio por esta origem. */
  fillinsShare: number;
}

export interface TicketByPlace {
  placeId: string;
  name: string;
  isActive: boolean;
  fillins: number;
  revenue: number;
  liters: number;
  avgTicket: number;
  litersPerFillin: number;
  pricePerLiter: number;
}

export interface TicketBenchmark {
  /** Mediana de ticket das redes que usam a alavanca. null se amostra < 3. */
  withLever: number | null;
  withoutLever: number | null;
  deltaPct: number | null;
  sampleWith: number;
  sampleWithout: number;
  /** Texto obrigatório: a UI NÃO pode omitir. Renderizar literalmente. */
  disclaimer: string;
}

export interface TicketLever {
  key: string;
  label: string;
  hint: string;
  enabled: boolean;
  benchmark: TicketBenchmark;
}

export interface TicketRecommendation {
  lever: string;
  label: string;
  deltaPct: number | null;
  text: string;
}

export interface ChainTicketData {
  id: 'chain-ticket';
  totals: {
    fillins: number;
    revenue: number;
    liters: number;
    avgTicket: number;
    litersPerFillin: number;
    pricePerLiter: number;
  };
  avgTicketQuintile: number | null;
  portfolioAvgTicketMedian: number | null;
  byOrigin: TicketByOrigin[];
  /** Todos os postos com linha no período, já ordenados por ticket desc. */
  byPlace: TicketByPlace[];
  topPlaces: TicketByPlace[];
  bottomPlaces: TicketByPlace[];
  levers: TicketLever[];
  /** Só vem preenchido quando a rede está no quintil inferior de ticket. */
  recommendations: TicketRecommendation[];
}

// ─── 3. chain-penetration ─────────────────────────────────────────────────────

export interface PenetrationBucket {
  bucket: string | null;
  deliveries: number;
  appDeliveries: number;
  totalValue: number;
  appValue: number;
  totalVolume: number;
  appPenetration: number | null;
}

export interface PenetrationPlace {
  placeId: string;
  name: string;
  isActive: boolean;
  deliveries: number;
  appDeliveries: number;
  totalValue: number;
  appValue: number;
  totalVolume: number;
  appPenetration: number | null;
  /** COUNT(DISTINCT) no grão dia×posto: não é somável. Só pico e média diários. */
  digitalCustomersDailyPeak: number;
  digitalCustomersDailyAvg: number;
}

export interface ChainPenetrationData {
  id: 'chain-penetration';
  /**
   * `false` = INDISPONÍVEL, não vazio e não erro: a rede não tem linha em
   * pump_delivery_daily. O motivo vem em `reason` e tem que ser exibido.
   */
  available: boolean;
  reason?: string;
  bucketUnit: BucketUnit;
  dataThrough: string | null;
  /** Só existe no payload disponível. */
  dataFrom?: string | null;
  overall: {
    deliveries: number;
    appDeliveries: number;
    pumpDeliveries: number;
    totalValue: number;
    appValue: number;
    appPenetration: number | null;
    previousAppPenetration: number | null;
    /** JÁ em pontos percentuais (×100). O front NÃO multiplica de novo. */
    deltaPp: number | null;
  } | null;
  series: PenetrationBucket[];
  byPlace: PenetrationPlace[];
  /** null de propósito: somar o distinct por posto dupla-contaria cliente. */
  digitalCustomers: number | null;
  digitalCustomersNote?: string;
}

// ─── 4. chain-customers ───────────────────────────────────────────────────────

export interface ChainCustomersData {
  id: 'chain-customers';
  totals: {
    base: number;
    withFillin: number;
    /** Cadastrou e nunca abasteceu: a conta de aquisição desperdiçada. */
    neverFilled: number;
    activationRate: number | null;
  };
  newCustomers: ChainKpi;
  activeCustomers: ChainKpi;
  lifecycle: {
    active: number;
    atRisk: number;
    inactive: number;
    lastFillinAt: string | null;
    /** Vêm da régua de recuperação da própria rede quando ela existe. */
    thresholds: { atRiskDays: number; inactiveDays: number };
  };
  bucketUnit: BucketUnit;
  series: { bucket: string | null; newCustomers: number }[];
}

// ─── 5. chain-modules ─────────────────────────────────────────────────────────

export interface ChainModule {
  key: string;
  label: string;
  /** Flag `use_*` da rede: o módulo está CONTRATADO. */
  enabled: boolean;
  /** Existe artefato do módulo vigente: o módulo está EM USO. */
  active: boolean;
  /** Preço declarado no cadastro. NUNCA é cobrança real. */
  declaredValue: number | null;
}

export interface ChainModulesData {
  id: 'chain-modules';
  modules: ChainModule[];
  enabledCount: number;
  activeCount: number;
  activeCountPrev: number;
  activeCountDelta: number;
  upsellPotential: number;
  /** Rótulo obrigatório do backend — renderizar junto do número. */
  upsellPotentialLabel: string;
  /** Módulos contratados e sem uso: risco de downgrade na renovação. */
  downgradeRisk: string[];
  note: string;
}

// ─── 6. chain-billing ─────────────────────────────────────────────────────────

export interface ImplantationStats {
  avg: number | null;
  p90: number | null;
  min: number | null;
  max: number | null;
  count: number;
}

export interface ImplantationPlace {
  placeId: string;
  name: string;
  isActive: boolean;
  monthlyPrice: number | null;
  gracePeriod: number | null;
  installationStartDate: string | null;
  createdAt: string | null;
  billingStartDate: string | null;
  firstBillAt: string | null;
  daysToBillingStart: number | null;
  daysToFirstBill: number | null;
  /** Posto que entrou na conta de receita não capturada. */
  uncaptured: boolean;
}

export interface ChainBillingData {
  id: 'chain-billing';
  mrr: number;
  places: { active: number; withBillingStart: number };
  period: {
    bills: number;
    value: number;
    paid: number;
    pending: number;
    canceled: number;
    retailerValue: number;
    /** Quanto da fatura fica com a revenda. */
    retailerShare: number | null;
  };
  overdue: { bills: number; value: number; oldestDueDate: string | null };
  lastBill: {
    id: string;
    status: string;
    type: string | null;
    value: number;
    dueDate: string | null;
    paymentDate: string | null;
    monthDate: string | null;
    competenceDate: string | null;
    createdAt: string | null;
    placeName: string | null;
  } | null;
  revenuePerFillin: {
    value: number | null;
    billed: number;
    fillins: number;
    matchedPlaces: number;
    note: string;
  };
  uncapturedRevenue: {
    places: number;
    value: number;
    /** Valorados pela mediana da carteira: é ESTIMATIVA, conta à parte. */
    estimatedPlaces: number;
    minFillins: number;
    portfolioMedianPrice: number | null;
  };
  implantation: {
    toBillingStart: ImplantationStats;
    toFirstBill: ImplantationStats;
    places: ImplantationPlace[];
  };
}

// ─── 7. chain-signals ─────────────────────────────────────────────────────────

export interface SignalPlace {
  placeId: string;
  name: string;
  /**
   * Posto PARADO: sem nenhum abastecimento nos últimos 7 dias.
   *
   * NÃO é `place.last_ping_at`: o ping só é escrito pelo pontuax-sync para
   * `sync_type = 'auto-system'` (114 dos 644 postos ativos), então a regra
   * antiga marcava como mudo o posto de toda rede que não usa auto-system.
   * Abastecimento existe para qualquer integração.
   */
  stalled: boolean;
  unresolvedErrors: number;
  lastErrorAt: string | null;
  lastConcentratorAt: string | null;
  /** 'YYYY-MM-DD' do último abastecimento; null = nenhum no período coberto. */
  lastFillinDate: string | null;
}

export interface TimelineEntry {
  id: string;
  entityType: string;
  entityId: string;
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
  origin: string | null;
  changedAt: string | null;
}

export interface ChainSignalsData {
  id: 'chain-signals';
  risk: {
    score: number;
    riskLevel: RiskLevel;
    /** Contrato, não enfeite: é o "por quê" do score, já em texto. */
    reasons: ChurnReason[];
    inputs: {
      fillins30d: number;
      fillinsPrev30d: number;
      stalledPlaces: number;
      activePlaces: number;
      newCustomers30d: number;
      activeCustomers30d: number;
      activeCustomersPrev30d: number;
      overdueBills: number;
      placesWithVolumeAndNoBilling: number;
      activeModules: number;
      activeModulesPrev: number;
      avgRating: number | null;
      ratings: number;
      /** Janela FIXA do score (30d), independente do filtro de período. */
      windowDays: number;
    };
  };
  places: SignalPlace[];
  summary: {
    activePlaces: number;
    /** Maior entre `noFillinPlaces` e `errorPlaces`, limitado a `activePlaces`. */
    stalledPlaces: number;
    /** Postos sem nenhum abastecimento nos últimos 7 dias. */
    noFillinPlaces: number;
    errorPlaces: number;
    overdueBills: number;
    overdueValue: number;
  };
  admin: {
    lastLoginAt: string | null;
    loginTrackingStartedAt: string | null;
  };
  timeline: TimelineEntry[];
}

/** Quantas linhas de timeline o backend devolve (LIMIT fixo). */
export const TIMELINE_LIMIT = 20;
